import apiClient from './api.service';
import type { ApiResponse } from '@/types';
import type { ApprovalResponsibilitySummary } from './user.service';

function unwrap<T>(res: ApiResponse<T>): T {
  if (res?.data !== undefined && res.data !== null) {
    return res.data as T;
  }
  return res as unknown as T;
}

export interface MocChangeType {
  id: string;
  company_id?: string | null;
  code: string;
  name: string;
  description?: string | null;
  sort_order: number;
  is_active: boolean;
}

/** Structured impact row (backend snake_case). */
export interface MocImpactRow {
  id?: string;
  dimension_code: string;
  impact_level: string;
  comments?: string | null;
  mitigation_notes?: string | null;
}

export interface MocRiskRow {
  id?: string;
  hazard_failure_mode: string;
  cause?: string | null;
  effect?: string | null;
  severity?: number | null;
  likelihood?: number | null;
  detectability?: number | null;
  risk_rating?: number | null;
  mitigation_controls?: string | null;
  residual_risk_after_mitigation?: string | null;
  sort_order?: number;
}

export interface MocNotificationRow {
  id?: string;
  target_kind: string;
  target_id?: string | null;
  target_label?: string | null;
  is_mandatory?: boolean;
  sort_order?: number;
}

export interface MocImplementationRow {
  id?: string;
  action_description: string;
  responsible_user_id?: string | null;
  target_date?: string | null;
  depends_on?: string | null;
  completed?: boolean;
  sort_order?: number;
}

export interface MocVerificationPayload {
  implementation_completed?: boolean;
  verification_performed?: boolean;
  effectiveness_confirmed?: boolean;
  adverse_impact_observed?: boolean;
  additional_action_required?: boolean;
  verification_evidence_notes?: string | null;
  verified_by_user_id?: string | null;
  verification_date?: string | null;
  closure_checklist_json?: Record<string, unknown> | null;
}

export interface MocStatusHistoryRow {
  id: string;
  from_status?: string | null;
  to_status: string;
  user_id?: string | null;
  remarks?: string | null;
  created_at: string;
}

export interface MocAttachmentRow {
  id?: string;
  attachment_kind?: string | null;
  title?: string | null;
  file_path?: string | null;
  document_id?: string | null;
}

export interface MocHeader {
  id: string;
  company_id?: string;
  moc_number: string;
  moc_date: string;
  change_title: string;
  change_type_id: string;
  change_category?: string | null;
  initiator_user_id: string;
  department?: string | null;
  department_id?: string | null;
  business_unit_site?: string | null;
  related_process_area?: string | null;
  related_work_order_id?: string | null;
  related_project_id?: string | null;
  related_supplier_id?: string | null;
  related_sales_order_id?: string | null;
  related_asset_id?: string | null;
  procedure_reference?: string | null;
  reason_for_change: string;
  detailed_description: string;
  business_justification?: string | null;
  effective_date_requested?: string | null;
  status: string;
  urgency: string;
  approval_instance_id?: string | null;
  customer_notification_required?: boolean;
  customer_notification_reason?: string | null;
  customer_notification_method?: string | null;
  customer_notification_sent_at?: string | null;
  customer_notification_ack_notes?: string | null;
  customer_communication_document_id?: string | null;
  manpower_impact_notes?: string | null;
  training_required_notes?: string | null;
  tooling_equipment_notes?: string | null;
  supplier_impact_notes?: string | null;
  documentation_updates_notes?: string | null;
  qualification_validation_notes?: string | null;
  implementation_prerequisites?: string | null;
  resource_readiness_comments?: string | null;
  implementation_plan?: string | null;
  implementation_dependencies?: string | null;
  risk_summary_score?: number | null;
  risk_assessment_complete?: boolean;
  changeType?: MocChangeType;
  impacts?: MocImpactRow[];
  risk_rows?: MocRiskRow[];
  notification_matrix?: MocNotificationRow[];
  implementation_actions?: MocImplementationRow[];
  verification?: MocVerificationPayload | null;
  attachments?: MocAttachmentRow[];
  status_history?: MocStatusHistoryRow[];
  created_at?: string;
  updated_at?: string;
}

export const MOC_IMPACT_DIMENSIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'product_quality', label: 'Product quality' },
  { code: 'process', label: 'Process' },
  { code: 'safety', label: 'Safety' },
  { code: 'supply_chain', label: 'Supply chain' },
  { code: 'delivery', label: 'Delivery' },
  { code: 'cost', label: 'Cost' },
  { code: 'compliance', label: 'Compliance / regulatory' },
  { code: 'customer', label: 'Customer' },
  { code: 'documentation', label: 'Documentation' },
  { code: 'resource', label: 'Resource' },
];

const base = '/moc';

export interface MocRegisterStats {
  total: number;
  open: number;
  pending_approval: number;
  pending_verification: number;
  pending_risk: number;
  implementation_active: number;
  high_risk_open: number;
  customer_notification_open: number;
}

export interface MocFormalReports {
  aging_open: { days_0_7: number; days_8_30: number; days_31_plus: number };
  by_change_type: Array<{ change_type_id: string; code: string; name: string; count: number }>;
  by_status: Array<{ status: string; count: number }>;
}

export const mocService = {
  async getChangeTypes(): Promise<MocChangeType[]> {
    const res = await apiClient.get(`${base}/change-types`);
    return unwrap(res);
  },

  async getRegisterStats(): Promise<MocRegisterStats> {
    const res = await apiClient.get(`${base}/stats`);
    return unwrap(res);
  },

  async getFormalReports(): Promise<MocFormalReports> {
    const res = await apiClient.get(`${base}/reports/formal`);
    return unwrap(res);
  },

  async listRelatedLinks(params: {
    work_order_id?: string;
    project_id?: string;
    supplier_id?: string;
    sales_order_id?: string;
    asset_id?: string;
  }): Promise<MocHeader[]> {
    const res = await apiClient.get(`${base}/related-links`, { params });
    return unwrap(res);
  },

  async list(params?: { status?: string; q?: string; preset?: string }): Promise<MocHeader[]> {
    const res = await apiClient.get(base, { params });
    return unwrap(res);
  },

  async getById(id: string): Promise<MocHeader> {
    const res = await apiClient.get(`${base}/${id}`);
    return unwrap(res);
  },

  async create(body: Record<string, unknown>): Promise<MocHeader> {
    const res = await apiClient.post(base, body);
    return unwrap(res);
  },

  async update(id: string, body: Record<string, unknown>): Promise<MocHeader> {
    const res = await apiClient.put(`${base}/${id}`, body);
    return unwrap(res);
  },

  async submit(id: string): Promise<MocHeader> {
    const res = await apiClient.post(`${base}/${id}/submit`);
    return unwrap(res);
  },

  async submitForApproval(id: string): Promise<MocHeader> {
    const res = await apiClient.post(`${base}/${id}/submit-for-approval`);
    return unwrap(res);
  },

  async promoteAfterApproval(id: string): Promise<MocHeader> {
    const res = await apiClient.post(`${base}/${id}/promote-after-approval`);
    return unwrap(res);
  },

  async startImplementation(id: string): Promise<MocHeader> {
    const res = await apiClient.post(`${base}/${id}/start-implementation`);
    return unwrap(res);
  },

  async requestVerification(id: string): Promise<MocHeader> {
    const res = await apiClient.post(`${base}/${id}/request-verification`);
    return unwrap(res);
  },

  async close(id: string): Promise<MocHeader> {
    const res = await apiClient.post(`${base}/${id}/close`);
    return unwrap(res);
  },

  async getHandover(id: string): Promise<MocHandoverView> {
    const res = await apiClient.get(`${base}/${id}/handover`);
    return unwrap(res);
  },

  async saveHandover(id: string, body: MocHandoverInput): Promise<MocHandoverView> {
    const res = await apiClient.put(`${base}/${id}/handover`, body);
    return unwrap(res);
  },

  async previewHandover(params: {
    from_user_id: string;
    to_user_id?: string;
    reason: MocHandoverReason;
    department_id?: string;
  }): Promise<{ items: MocHandoverItem[]; warnings: string[] }> {
    const res = await apiClient.get(`${base}/handover/preview`, { params });
    return unwrap(res);
  },

  async rerunHandover(id: string): Promise<MocHandoverView> {
    const res = await apiClient.post(`${base}/${id}/handover/execute`);
    return unwrap(res);
  },

  async extendHandover(id: string, effectiveTo: string): Promise<MocHandoverView> {
    const res = await apiClient.put(`${base}/${id}/handover/extend`, { effective_to: effectiveTo });
    return unwrap(res);
  },

  async getResponsibilities(userId: string): Promise<UserApprovalResponsibilities> {
    const res = await apiClient.get(`${base}/handover/responsibilities`, { params: { user_id: userId } });
    return unwrap(res);
  },
};

export const MOC_CHANGE_TYPE_KEY_PERSONNEL = 'KEY_PERSONNEL';

export type MocHandoverReason = 'leave' | 'resignation' | 'termination' | 'other';

export const MOC_HANDOVER_REASONS: ReadonlyArray<{ value: MocHandoverReason; label: string }> = [
  { value: 'leave', label: 'Leave (temporary delegation)' },
  { value: 'resignation', label: 'Resignation (permanent handover)' },
  { value: 'termination', label: 'Termination (permanent handover)' },
  { value: 'other', label: 'Other (permanent handover)' },
];

export interface MocHandoverItem {
  item_type: string;
  item_label: string;
  ref_table: string;
  ref_id: string;
  label: string;
  mandatory: boolean;
  selected: boolean;
  executed?: boolean;
  old_value?: string | null;
  new_value?: string | null;
  error?: string | null;
  executed_at?: string | null;
}

export interface MocHandoverRecord {
  id: string;
  moc_id: string;
  from_user_id: string;
  to_user_id: string;
  from_user_name?: string;
  to_user_name?: string;
  handover_kind: 'temporary' | 'permanent';
  reason: MocHandoverReason;
  effective_from: string | null;
  effective_to: string | null;
  status: 'planned' | 'executed' | 'active' | 'ended';
  executed_at: string | null;
  executed_by: string | null;
}

export interface MocHandoverView {
  available: boolean;
  handover: MocHandoverRecord | null;
  items: MocHandoverItem[];
  warnings: string[];
}

export interface MocHandoverInput {
  from_user_id: string;
  to_user_id: string;
  reason: MocHandoverReason;
  effective_from?: string | null;
  effective_to?: string | null;
  items?: Array<{ item_type: string; ref_id: string; selected: boolean }>;
}

export type UserApprovalResponsibilities = ApprovalResponsibilitySummary;
